import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ModalTemplate from './ModalTemplate';
import { deviceAPI } from '../../API/deviceAPI';
import { fmtDateTime } from '../../Utils/format';

const formatDuration = (startDate, endDate) => {
  if (!startDate) {
    return '—';
  }

  const start = new Date(startDate).getTime();
  const end = endDate ? new Date(endDate).getTime() : Date.now();
  const diffMs = Math.max(0, end - start);
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    if (remainingHours > 0) {
      return `${diffDays}d ${remainingHours}h`;
    }
    return `${diffDays}d`;
  }

  if (diffHours > 0) {
    return `${diffHours}h`;
  }

  const diffMins = Math.floor(diffMs / (1000 * 60));
  return `${diffMins}m`;
};

const DeviceStoreHistoryModal = ({
  open,
  onClose,
  deviceId,
  deviceName,
}) => {
  const { t } = useTranslation(['devices', 'common']);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!deviceId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await deviceAPI.getDeviceHistory(deviceId);
      if (response && !response.error) {
        const rawData = response.data || response;
        const list = Array.isArray(rawData) ? rawData : (rawData?.value || []);
        // Sort descending: most recent stint first
        const sorted = [...list].sort((a, b) => {
          const dateA = new Date(a.associatedAt || 0).getTime();
          const dateB = new Date(b.associatedAt || 0).getTime();
          return dateB - dateA;
        });
        setHistory(sorted);
      } else {
        setError(t('devices:historyModal.error', 'Erro ao carregar histórico de atribuição.'));
      }
    } catch (err) {
      setError(t('devices:historyModal.error', 'Erro ao carregar histórico de atribuição.'));
    } finally {
      setLoading(false);
    }
  }, [deviceId, t]);

  useEffect(() => {
    if (open && deviceId) {
      fetchHistory();
    }
  }, [open, deviceId, fetchHistory]);

  return (
    <ModalTemplate
      open={Boolean(open)}
      title={
        <Box>
          <Typography variant="h3">
            {t('devices:historyModal.title', 'Histórico de Atribuição a Lojas')}
          </Typography>
          {deviceName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {deviceName}
            </Typography>
          )}
        </Box>
      }
      size="sm"
      icon={<HistoryIcon color="primary" />}
      onClose={onClose}
      content={
        <Box sx={{ py: 1 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchHistory}
                sx={{ mt: 1.5 }}
              >
                {t('common:buttons.retry', 'Tentar novamente')}
              </Button>
            </Box>
          )}

          {!loading && !error && history.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <StorefrontIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
              <Typography variant="body2">
                {t('devices:historyModal.empty', 'Nenhum histórico de atribuição registado para este dispositivo.')}
              </Typography>
            </Box>
          )}

          {!loading && !error && history.length > 0 && (
            <Stack spacing={0} sx={{ mt: 1 }}>
              {history.map((stint, index) => {
                const isCurrent = !stint.disassociatedAt;
                const isLast = index === history.length - 1;
                const durationText = formatDuration(stint.associatedAt, stint.disassociatedAt);

                return (
                  <Box
                    key={stint.id || `${stint.storeId}-${stint.associatedAt}`}
                    sx={{ display: 'flex', position: 'relative' }}
                  >
                    {/* Timeline line and dot indicator */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mr: 2,
                        minWidth: 28,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isCurrent ? 'success.lighter' : 'action.hover',
                          color: isCurrent ? 'success.main' : 'text.secondary',
                          border: '2px solid',
                          borderColor: isCurrent ? 'success.main' : 'divider',
                          zIndex: 1,
                        }}
                      >
                        {isCurrent ? (
                          <CheckCircleIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <StorefrontIcon sx={{ fontSize: 13 }} />
                        )}
                      </Box>
                      {!isLast && (
                        <Box
                          sx={{
                            width: 2,
                            flexGrow: 1,
                            bgcolor: 'divider',
                            my: 0.5,
                            minHeight: 28,
                          }}
                        />
                      )}
                    </Box>

                    {/* Timeline item content */}
                    <Box
                      sx={{
                        flex: 1,
                        pb: isLast ? 1 : 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          {stint.storeName || t('devices:detail.infoCard.store', 'Loja')}
                        </Typography>

                        <Chip
                          label={
                            isCurrent
                              ? t('devices:historyModal.active', 'Ativa')
                              : t('devices:historyModal.historical', 'Histórico')
                          }
                          color={isCurrent ? 'success' : 'default'}
                          size="small"
                          variant={isCurrent ? 'filled' : 'outlined'}
                          sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </Box>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 0.5, sm: 2 }}
                        sx={{ mt: 0.75 }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('devices:historyModal.associatedAt', 'Associado em')}
                          </Typography>
                          <Typography variant="body2">
                            {fmtDateTime(stint.associatedAt)}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('devices:historyModal.disassociatedAt', 'Desassociado em')}
                          </Typography>
                          <Typography variant="body2">
                            {stint.disassociatedAt
                              ? fmtDateTime(stint.disassociatedAt)
                              : t('devices:historyModal.present', 'Presente')}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('devices:historyModal.duration', 'Duração')}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            {durationText}
                          </Typography>
                        </Box>
                      </Stack>

                      {stint.reason && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('devices:historyModal.reason', 'Motivo')}: {stint.reason}
                          </Typography>
                        </Box>
                      )}

                      {!isLast && <Divider sx={{ mt: 1.5, opacity: 0.6 }} />}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      }
    />
  );
};

DeviceStoreHistoryModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  deviceId: PropTypes.string,
  deviceName: PropTypes.string,
};

export default DeviceStoreHistoryModal;
